<script setup>
  import { computed, ref, watch } from "vue";
  import {
    useChartTemplatesStore,
    getBaseTypeLabel,
    isPieType as isPieTypeCheck,
    isClusteredType as isClusteredTypeCheck,
    STACKED_TYPES,
  } from "@stores/chartTemplates/chartTemplates";
  import { Card, CardContent } from "@/components/ui/card";
  import { Input } from "@/components/ui/input";
  import { Label } from "@/components/ui/label";
  import { Select } from "@/components/episto-ui/select";
  import { Badge } from "@/components/ui/badge";
  import ChartPreview from "@/components/Reports/ChartTemplates/ChartPreview.vue";
  import { OFFICE_PREVIEW_COLORS } from "@/lib/chartPalettes";

  const paletteOptions = [
    { value: "default", label: "Default" },
    { value: "nps",     label: "NPS" },
  ];

  const props = defineProps({
    template: {
      type: Object,
      required: true,
    },
    allCharts: {
      type: Array,
      default: () => [],
    },
  });

  const store = useChartTemplatesStore();

  const previewSeries = ref(4);
  const previewCategories = ref(1);

  const effectiveData = computed(() => {
    if (props.template) {
      return store.getEffectiveTemplateData(props.template.id) || props.template;
    }
    return { name: "", colors: [] };
  });

  const isPieType = computed(() => isPieTypeCheck(effectiveData.value?.base_type));
  const isClusteredType = computed(() => isClusteredTypeCheck(effectiveData.value?.base_type));

  watch(
    () => props.template?.id,
    () => {
      previewSeries.value = 4;
      previewCategories.value = isPieTypeCheck(props.template?.base_type) ? 4 : 1;
    },
    { immediate: true },
  );

  watch(isPieType, (pie) => {
    previewCategories.value = pie ? 4 : 1;
  });

  const seriesOptions = [2, 3, 4, 5, 6];
  const categoryOptions = [1, 2, 3, 4, 5, 6];

  const typeLabel = computed(() => getBaseTypeLabel(effectiveData.value?.base_type));

  const isStackedBaseType = computed(() => STACKED_TYPES.includes(effectiveData.value?.base_type));

  const chartTypeValue = computed(
    () => effectiveData.value?.chart_type || effectiveData.value?.base_type?.toLowerCase() || null,
  );

  const isNpsType = computed(() => chartTypeValue.value === "nps");

  const chartTypeOptions = computed(() => [
    { value: effectiveData.value?.base_type?.toLowerCase(), label: `Standard (${typeLabel.value})` },
    { value: "nps", label: "NPS", disabled: !isStackedBaseType.value },
  ]);

  function handleChartTypeChange(value) {
    if (!props.template) return;
    store.updateLocalTemplate(props.template.id, "chart_type", value);
    store.updateLocalTemplate(props.template.id, "report_theme_palette", value === "nps" ? "nps" : "default");
  }

  const chartOptions = computed(() =>
    props.allCharts.map((c) => ({
      value: `${c.slide_index}-${c.chart_index}`,
      label: `Slide ${c.slide_index + 1} — ${c.shape_name || `Chart ${c.chart_index + 1}`}`,
    })),
  );

  const sourceChartValue = computed(() => {
    const data = effectiveData.value;
    if (data.source_slide_index == null || data.source_chart_index == null) return null;
    return `${data.source_slide_index}-${data.source_chart_index}`;
  });

  function handleSourceChartChange(value) {
    if (!props.template || !value) return;
    const [slideIndex, chartIndex] = value.split("-").map(Number);
    const chart = props.allCharts.find((c) => c.slide_index === slideIndex && c.chart_index === chartIndex);
    if (chart) store.setSourceChart(props.template.id, chart);
  }

  function handleNameChange(value) {
    if (props.template) store.updateLocalTemplate(props.template.id, "name", value);
  }
</script>

<template>
  <Card class="h-full flex flex-col overflow-hidden">
    <CardContent class="flex-1 overflow-y-auto p-6 space-y-6">
      <!-- Template name -->
      <div class="space-y-2">
        <Label>Template Name</Label>
        <Input
          :model-value="effectiveData.name"
          placeholder="Chart template name..."
          class="max-w-xs"
          @update:model-value="handleNameChange" />
      </div>

      <!-- Source chart selector -->
      <div class="space-y-2">
        <Label>Source Chart</Label>
        <Select
          :model-value="sourceChartValue"
          :options="chartOptions"
          placeholder="Select a chart..."
          class="max-w-xs"
          @update:model-value="handleSourceChartChange($event)" />
      </div>

      <!-- Chart type (readonly, shown after source is selected) -->
      <div
        v-if="effectiveData.base_type"
        class="space-y-2">
        <Label>Chart Type</Label>
        <Badge variant="outline">{{ typeLabel }}</Badge>
      </div>

      <div
        v-if="effectiveData.base_type"
        class="space-y-2">
        <Label>Type</Label>
        <Select
          :model-value="chartTypeValue"
          :options="chartTypeOptions"
          class="max-w-xs"
          @update:model-value="handleChartTypeChange" />
      </div>

      <!-- Palette -->
      <div class="space-y-2">
        <Label>Palette</Label>
        <Select
          :model-value="effectiveData.report_theme_palette || 'default'"
          :options="paletteOptions"
          class="max-w-xs"
          @update:model-value="(v) => store.updateLocalTemplate(props.template?.id, 'report_theme_palette', v)" />
      </div>

      <!-- Preview section -->
      <div
        v-if="effectiveData.base_type"
        class="space-y-3 border-t pt-6">
        <div>
          <Label>Preview</Label>
        </div>

        <!-- Preview controls -->
        <div class="flex items-center gap-6">
          <div
            v-if="!isPieType && !isNpsType"
            class="flex items-center gap-2">
            <span class="text-sm text-muted-foreground">Answer options:</span>
            <div class="flex rounded-md border border-input overflow-hidden">
              <button
                v-for="n in seriesOptions"
                :key="n"
                :class="[
                  'px-3 py-1.5 text-sm font-medium transition-colors',
                  previewSeries === n ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted',
                ]"
                @click="previewSeries = n">
                {{ n }}
              </button>
            </div>
          </div>

          <div class="flex items-center gap-2">
            <span class="text-sm text-muted-foreground">{{ isPieType ? "Answer options:" : "Respondent groups:" }}</span>
            <div class="flex rounded-md border border-input overflow-hidden">
              <button
                v-for="n in categoryOptions"
                :key="n"
                :class="[
                  'px-3 py-1.5 text-sm font-medium transition-colors',
                  previewCategories === n ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted',
                ]"
                @click="previewCategories = n">
                {{ n }}
              </button>
            </div>
          </div>
        </div>

        <!-- Chart preview -->
        <div class="bg-white rounded-lg border p-4 flex items-center justify-center">
          <ChartPreview
            :base-type="effectiveData.base_type"
            :colors="OFFICE_PREVIEW_COLORS"
            :width="400"
            :height="250"
            :series-count="isNpsType ? 3 : isClusteredType ? previewCategories : previewSeries"
            :category-count="isClusteredType ? previewSeries : previewCategories" />
        </div>
      </div>
    </CardContent>
  </Card>
</template>
